import type { Question } from '../types'
import { createDataVariantPack } from './data_authored_variants'
import { createLogicVariantPack } from './logic_authored_variants'
import { createMathVariantPack } from './math_authored_variants'

const newSeed = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID()
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`
}

const asHsaPracticeItem = (question: Question): Question => ({
  ...question,
  id: `HSA-GEN-${question.id}`,
  exam: 'HSA',
  variantGroup: `hsa-${question.variantGroup}`,
  tags: [...question.tags, 'hsa-compatible'],
})

/** TSA practice stays local until a matching reviewed Worker template exists.
 * Each fresh seed creates new values while preserving a complete answer key. */
const asTsaPracticeItem = (question: Question): Question => ({
  ...question,
  id: `TSA-GEN-${question.id}`,
  exam: 'TSA',
  domain: 'Tư duy Toán học',
  variantGroup: `tsa-${question.variantGroup}`,
  tags: [...question.tags, 'tsa-compatible', 'tsa-math-generator'],
})

/**
 * Adds a new set of deterministic, original question variants for every
 * adaptive practice session. A seed uniquely identifies the item and locks
 * its data, answer, hints and solution together for attempt persistence.
 */
export function createPracticeQuestionBank(baseQuestions: Question[], seed = newSeed()): Question[] {
  const math = createMathVariantPack(seed)
  const data = createDataVariantPack(seed)
  const logic = createLogicVariantPack(seed)
  const sessionVariants = [...math, ...data, ...logic].map(question => ({
    ...question,
    tags: [...question.tags, 'session-variant'],
  }))
  const hsaCompatible = sessionVariants
    .filter(question => question.domain !== 'Logic')
    .map(asHsaPracticeItem)

  // The parameterized math families are an honest “unlimited” layer: every
  // seed has fresh numbers, but only reviewed families are generated.
  const tsaCompatible = math.map(question => asTsaPracticeItem({
    ...question,
    tags: [...question.tags, 'session-variant'],
  }))

  return [...baseQuestions, ...sessionVariants, ...hsaCompatible, ...tsaCompatible]
}
